const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.querySelectorAll(`.${SCROLL_ANIMATION_TRIGGER_CLASSNAME}`));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        const elementTarget = entry.target;

        if (entry.isIntersecting) {
          elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
          if (elementTarget.hasAttribute('data-cascade')) {
            elementTarget.setAttribute('style', `--animation-order: ${elementTarget.dataset.index || 0};`);
          }
          observer.unobserve(elementTarget);
        } else {
          elementTarget.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        }
      });
    },
    {
      rootMargin: '0px 0px 600px 0px',
      threshold: 0.01, // L'animation démarre dès que 10% de l'élément est visible
    },
  );

  const MAX_ORDER = 5;
  const totalCount = animationTriggerElements.length - 1 || 1; // éviter division par 0

  animationTriggerElements.forEach((element, index) => {
    // On calcule une fraction entre 0 et 1, puis on la multiplie par MAX_ORDER
    const fraction = index / totalCount;
    const scaledIndex = Math.round(fraction * MAX_ORDER);
    element.setAttribute('data-index', scaledIndex);
    observer.observe(element);
  });
}

// Zoom in animation logic optimisé pour l'INP
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;
  let ticking = false;
  const elementsData = new Map();

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
        elementsData.set(element, {
          isVisible: elementIsVisible,
          lastPercentage: -1,
          cachedRect: null,
        });
      });
    });
    observer.observe(element);

    // Initialisation avec requestAnimationFrame
    requestAnimationFrame(() => {
      element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
    });

    elementsData.set(element, {
      isVisible: elementIsVisible,
      lastPercentage: -1,
      cachedRect: null,
    });
  });

  // Optimiser le scroll listener avec requestAnimationFrame
  const updateZoomAnimations = () => {
    elementsData.forEach((data, element) => {
      if (!data.isVisible) return;

      const currentPercentage = percentageSeen(element);

      // Éviter les recalculs inutiles
      if (Math.abs(currentPercentage - data.lastPercentage) > 1) {
        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * currentPercentage);
        data.lastPercentage = currentPercentage;
      }
    });
    ticking = false;
  };

  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        requestAnimationFrame(updateZoomAnimations);
        ticking = true;
      }
    },
    { passive: true },
  );
}

// Cache pour optimiser les calculs de percentageSeen
const elementCache = new WeakMap();

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;

  // Utiliser le cache pour éviter les recalculs de getBoundingClientRect
  let cachedData = elementCache.get(element);
  if (!cachedData || Math.abs(cachedData.lastScrollY - scrollY) > 50) {
    const rect = element.getBoundingClientRect();
    cachedData = {
      elementPositionY: rect.top + scrollY,
      elementHeight: element.offsetHeight,
      lastScrollY: scrollY,
    };
    elementCache.set(element, cachedData);
  }

  const { elementPositionY, elementHeight } = cachedData;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

// Différer l'initialisation des animations pour améliorer l'INP
function initializeAnimations() {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
}

if (document.readyState === 'loading') {
  window.addEventListener('DOMContentLoaded', () => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(initializeAnimations);
    } else {
      setTimeout(initializeAnimations, 0);
    }
  });
} else {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(initializeAnimations);
  } else {
    setTimeout(initializeAnimations, 0);
  }
}

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
